import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Award, BookOpen, GraduationCap, Loader2, LockKeyhole, LogOut, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { apiError } from "@/lib/api";
import { shortDate } from "@/lib/format";
import { apprenticeApi, APPRENTICE_TOKEN_KEY } from "@/lib/apprenticeApi";

const API_BASE = `${BACKEND_URL}/api`;
const SCOPES = [["website", "Our website"], ["print", "Printed material"],
  ["social", "Social media"]];
const OUTCOME_STYLE = {
  pass: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  concerns: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  fail: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};

const Tile = ({ label, value, sub, testId }) => (
  <div className="rounded-[14px] border border-border bg-card p-4" data-testid={testId}>
    <div className="text-[10.5px] uppercase tracking-[1.4px] text-muted-foreground">{label}</div>
    <div className="mt-1 font-display text-2xl font-bold text-foreground">{value}</div>
    {sub && <div className="mt-0.5 text-[11.5px] text-muted-foreground">{sub}</div>}
  </div>
);

export const ApprenticeSignin = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const signIn = async () => {
    setBusy(true);
    try {
      const res = await apprenticeApi.post("/apprentice/login", form);
      localStorage.setItem(APPRENTICE_TOKEN_KEY, res.data.access_token);
      navigate("/apprentice", { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4 py-10">
      <div className="panel w-full max-w-sm p-7" data-testid="apprentice-signin-card">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-gradient-to-br from-primary to-[hsl(var(--teal))]">
            <GraduationCap className="h-4 w-4 text-white" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">
              Apprentice account
            </div>
            <div className="font-display text-[17px] font-bold text-foreground">Sign in</div>
          </div>
        </div>
        <div className="mt-5 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">Email address</Label>
            <Input value={form.email} data-testid="apprentice-email-input" autoComplete="username"
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="bg-muted/40 font-mono text-[12.5px]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">Password</Label>
            <Input type="password" value={form.password} data-testid="apprentice-password-input"
                   autoComplete="current-password"
                   onKeyDown={(e) => e.key === "Enter" && signIn()}
                   onChange={(e) => setForm({ ...form, password: e.target.value })}
                   className="bg-muted/40" />
          </div>
          <Button onClick={signIn} data-testid="apprentice-signin-button"
                  disabled={busy || !form.email || !form.password}
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <LockKeyhole className="mr-2 h-4 w-4" />} Sign in
          </Button>
        </div>
        <p className="mt-5 text-[10.5px] text-muted-foreground">
          Your coordinator opens your account and gives you the password.
        </p>
      </div>
    </div>
  );
};

export default function ApprenticePortal() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [details, setDetails] = useState(null);
  const [consent, setConsent] = useState({ granted: false, scopes: [] });
  const [diary, setDiary] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await apprenticeApi.get("/apprentice/me");
    setMe(res.data);
    setDetails({ phone: res.data.phone || "", address: res.data.address || "",
      education: res.data.education || "" });
    setConsent({ granted: Boolean(res.data.consent?.granted),
      scopes: res.data.consent?.scopes || [] });
  }, []);

  useEffect(() => {
    load().catch((e) => {
      if (e?.response?.status === 401) navigate("/apprentice/signin", { replace: true });
      else toast.error(apiError(e));
    });
  }, [load, navigate]);

  const act = async (key, fn, message) => {
    setBusy(key);
    let ok = false;
    try {
      const res = await fn();
      toast.success(typeof message === "function" ? message(res) : message);
      await load();
      ok = true;
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
    return ok;
  };

  const signOut = async () => {
    await apprenticeApi.post("/apprentice/logout").catch(() => {});
    localStorage.removeItem(APPRENTICE_TOKEN_KEY);
    navigate("/apprentice/signin", { replace: true });
  };

  const saveDiary = async () => {
    const ok = await act("diary", () => apprenticeApi.post("/apprentice/diary", {
      ...diary, hours: Number(diary.hours),
    }), (res) => res.data.message);
    if (ok) setDiary(null);
  };

  if (!me || !details) {
    return <div className="grid min-h-screen place-items-center bg-background">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  const done = (me.training_plan || []).filter((m) => m.done).length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/70 px-5 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {me.photo_url
              ? <img alt="" data-testid="apprentice-photo"
                     src={`${BACKEND_URL}${me.photo_url}`}
                     className="h-9 w-9 rounded-full object-cover" />
              : <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-gradient-to-br from-primary to-[hsl(var(--teal))]">
                  <GraduationCap className="h-4 w-4 text-white" />
                </span>}
            <div>
              <div className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">
                {me.apprentice_ref} · {me.trade}
              </div>
              <div className="font-display text-[16px] font-bold text-foreground"
                   data-testid="apprentice-name">{me.name}</div>
            </div>
          </div>
          <Button variant="outline" onClick={signOut} data-testid="apprentice-signout"
                  className="rounded-full text-[12px]">
            <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-5 px-5 py-7">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile testId="apprentice-status" label="Where I am"
                value={me.status.replace(/_/g, " ")}
                sub={me.placement ? me.placement.department : "Not placed yet"} />
          <Tile testId="apprentice-modules" label="Modules done"
                value={`${done}/${(me.training_plan || []).length}`} />
          <Tile testId="apprentice-hours" label="Approved hours" value={me.hours.approved}
                sub={me.hours.waiting ? `${me.hours.waiting} awaiting approval` : "All approved"} />
          <Tile testId="apprentice-rating" label="Average review"
                value={me.average_rating ? `${me.average_rating}/5` : "—"}
                sub={`${(me.reviews || []).filter((r) => r.status === "recorded").length} signed off`} />
        </div>

        <Tabs defaultValue="reviews">
          <TabsList data-testid="apprentice-tabs">
            <TabsTrigger value="reviews" data-testid="tab-my-reviews">My reviews</TabsTrigger>
            <TabsTrigger value="plan" data-testid="tab-my-plan">Training plan</TabsTrigger>
            <TabsTrigger value="diary" data-testid="tab-my-diary">My diary</TabsTrigger>
            <TabsTrigger value="me" data-testid="tab-my-details">My details</TabsTrigger>
          </TabsList>

          <TabsContent value="reviews" className="mt-4 space-y-4">
            <section className="rounded-[14px] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground md:text-lg">
                Placement and reviews
              </h2>
              {me.placement && (
                <p className="mt-1.5 text-[12.5px] text-muted-foreground"
                   data-testid="apprentice-placement">
                  {me.placement.department} · supervised by {me.placement.supervisor} ·{" "}
                  {shortDate(me.placement.starts_on)} → {shortDate(me.placement.ends_on)}
                  {me.placement.location ? ` · ${me.placement.location}` : ""}
                </p>
              )}
              <ul className="mt-3 space-y-2" data-testid="apprentice-reviews">
                {(me.reviews || []).length === 0 && (
                  <li className="text-[12.5px] text-muted-foreground">
                    Your review points appear once your placement starts.
                  </li>
                )}
                {(me.reviews || []).map((r) => (
                  <li key={r.index} data-testid={`my-review-${r.index}`}
                      className="rounded-xl border border-border px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-[13px] text-foreground">{r.label}</span>
                      {r.status === "recorded" ? (
                        <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] ${OUTCOME_STYLE[r.outcome]}`}>
                          {r.outcome} · {r.rating}/5
                        </span>
                      ) : (
                        <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                          due {shortDate(r.due_on)}
                        </span>
                      )}
                    </div>
                    {r.note && (
                      <p className="mt-1 text-[12px] text-muted-foreground">{r.note}</p>
                    )}
                  </li>
                ))}
              </ul>
              {me.certificate_url && (
                <a href={`${API_BASE}/apprentice/certificate.pdf`} target="_blank" rel="noreferrer"
                   data-testid="my-certificate"
                   className="mt-4 inline-flex items-center rounded-full bg-primary px-4 py-2 text-[12px] text-primary-foreground transition-opacity duration-150 hover:opacity-90">
                  <Award className="mr-1.5 h-3.5 w-3.5" /> My certificate
                </a>
              )}
            </section>
          </TabsContent>

          <TabsContent value="plan" className="mt-4">
            <section className="rounded-[14px] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground md:text-lg">Training plan</h2>
              <p className="mt-1 text-[12px] text-muted-foreground">
                Your supervisor ticks these off as you complete them.
              </p>
              <ul className="mt-3 space-y-1.5" data-testid="apprentice-plan">
                {(me.training_plan || []).map((m, i) => (
                  <li key={i} data-testid={`my-module-${i}`}
                      className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
                    <span className="text-[12.5px] text-foreground">{m.module}</span>
                    <span className={`font-mono text-[10.5px] ${m.done
                      ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                      {m.done ? "complete" : "in progress"}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          </TabsContent>

          <TabsContent value="diary" className="mt-4">
            <section className="rounded-[14px] border border-border bg-card p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-base font-semibold text-foreground md:text-lg">
                  <BookOpen className="h-4 w-4 text-primary" /> My diary
                </h2>
                <Button size="sm" data-testid="add-diary-button"
                        onClick={() => setDiary({ on: new Date().toISOString().slice(0, 10),
                          hours: 4, what_i_did: "", what_i_learned: "" })}
                        className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Write up a day
                </Button>
              </div>
              <p className="mt-1 text-[12px] text-muted-foreground">
                The hours you log here go to your supervisor to approve.
              </p>
              <ul className="mt-3 space-y-2" data-testid="apprentice-diary">
                {(me.diary || []).length === 0 && (
                  <li className="text-[12.5px] text-muted-foreground">
                    Nothing written up yet.
                  </li>
                )}
                {(me.diary || []).map((d) => (
                  <li key={d.on} data-testid={`diary-${d.on}`}
                      className="rounded-xl border border-border px-3 py-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[12.5px] font-medium text-foreground">
                        {shortDate(d.on)}
                      </span>
                      <span className="font-mono text-[10.5px] text-muted-foreground">
                        {d.hours}h · {d.time_entry_ref}
                      </span>
                    </div>
                    <p className="mt-1 text-[12px] text-foreground">{d.what_i_did}</p>
                    {d.what_i_learned && (
                      <p className="mt-1 text-[12px] text-muted-foreground">
                        What I learned: {d.what_i_learned}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          </TabsContent>

          <TabsContent value="me" className="mt-4 space-y-4">
            <section className="space-y-3 rounded-[14px] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground md:text-lg">My details</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Telephone</Label>
                  <Input value={details.phone} data-testid="apprentice-phone"
                         onChange={(e) => setDetails({ ...details, phone: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Address</Label>
                  <Input value={details.address} data-testid="apprentice-address"
                         onChange={(e) => setDetails({ ...details, address: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">Education</Label>
                <Textarea rows={3} value={details.education} data-testid="apprentice-education"
                          onChange={(e) => setDetails({ ...details, education: e.target.value })} />
              </div>
              <Button onClick={() => act("details",
                        () => apprenticeApi.put("/apprentice/me", details), "Your details are saved")}
                      disabled={busy === "details"} data-testid="apprentice-save-details"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                Save my details
              </Button>
            </section>

            <section className="space-y-3 rounded-[14px] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground md:text-lg">
                Photographs and my story
              </h2>
              <p className="text-[12.5px] text-muted-foreground">
                You choose where we may use your name, photograph and story, and you can change your
                mind at any time.
              </p>
              <label className="flex items-start gap-2.5 rounded-xl border border-border bg-muted/25 p-3">
                <Checkbox checked={consent.granted} data-testid="apprentice-consent-granted"
                          onCheckedChange={(v) => setConsent({ ...consent, granted: Boolean(v) })} />
                <span className="text-[12.5px] text-foreground">
                  I give permission for my name, photograph and story to be used.
                </span>
              </label>
              {consent.granted && (
                <div className="flex flex-wrap gap-3 pl-1" data-testid="apprentice-consent-scopes">
                  {SCOPES.map(([code, text]) => (
                    <label key={code} className="flex items-center gap-2">
                      <Checkbox checked={consent.scopes.includes(code)}
                                data-testid={`apprentice-consent-${code}`}
                                onCheckedChange={() => setConsent({
                                  ...consent,
                                  scopes: consent.scopes.includes(code)
                                    ? consent.scopes.filter((s) => s !== code)
                                    : [...consent.scopes, code],
                                })} />
                      <span className="text-[12px] text-muted-foreground">{text}</span>
                    </label>
                  ))}
                </div>
              )}
              <Button onClick={() => act("consent", () => apprenticeApi.post("/apprentice/consent", {
                        granted: consent.granted && consent.scopes.length > 0,
                        scopes: consent.granted ? consent.scopes : [],
                      }), consent.granted && consent.scopes.length > 0
                        ? "Permission recorded" : "Permission withdrawn")}
                      disabled={busy === "consent"} data-testid="apprentice-save-consent"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {consent.granted && consent.scopes.length > 0
                  ? "Save my permission" : "Withdraw my permission"}
              </Button>
            </section>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={Boolean(diary)} onOpenChange={(o) => !o && setDiary(null)}>
        <DialogContent data-testid="diary-dialog">
          <DialogHeader>
            <DialogTitle>Write up a day</DialogTitle>
            <DialogDescription>
              Say what you did and what you learned. The hours go to your supervisor to approve.
            </DialogDescription>
          </DialogHeader>
          {diary && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Day</Label>
                  <Input type="date" value={diary.on} data-testid="diary-date"
                         onChange={(e) => setDiary({ ...diary, on: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Hours</Label>
                  <Input type="number" step="0.5" value={diary.hours} data-testid="diary-hours"
                         onChange={(e) => setDiary({ ...diary, hours: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">
                  What I did (10 characters or more)
                </Label>
                <Textarea rows={3} value={diary.what_i_did} data-testid="diary-did"
                          onChange={(e) => setDiary({ ...diary, what_i_did: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">What I learned</Label>
                <Textarea rows={2} value={diary.what_i_learned} data-testid="diary-learned"
                          onChange={(e) => setDiary({ ...diary, what_i_learned: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveDiary} data-testid="diary-save"
                    disabled={busy === "diary" || (diary?.what_i_did || "").trim().length < 10}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "diary" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save my entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
